import { useRef, useState } from 'react'
import { FileText, Eye, Trash, UploadSimple, Spinner, DownloadSimple } from '@phosphor-icons/react'
import { DocumentViewer } from './DocumentViewer'
import type { AttachedDocument } from '../services/quoteDocuments'

interface DocumentAttachmentsProps {
  documents: AttachedDocument[]
  // When provided, the component renders an upload control and a remove button.
  // Omit (or pass readOnly) to render a view-only list.
  onChange?: (docs: AttachedDocument[]) => void
  uploader?: (file: File) => Promise<AttachedDocument>
  readOnly?: boolean
  emptyText?: string
}

const ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,application/pdf,image/*'

export function DocumentAttachments({
  documents,
  onChange,
  uploader,
  readOnly,
  emptyText = 'Nenhum documento anexado',
}: DocumentAttachmentsProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [viewing, setViewing] = useState<AttachedDocument | null>(null)

  const canEdit = !readOnly && !!onChange && !!uploader

  async function handleFiles(files: FileList | null) {
    if (!files || !uploader || !onChange) return
    setError(null)
    setUploading(true)
    try {
      const uploaded: AttachedDocument[] = []
      for (const file of Array.from(files)) {
        uploaded.push(await uploader(file))
      }
      onChange([...documents, ...uploaded])
    } catch (err: any) {
      setError(err.message || 'Erro ao enviar documento')
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  function removeAt(idx: number) {
    if (!onChange) return
    onChange(documents.filter((_, i) => i !== idx))
  }

  return (
    <div className="space-y-3">
      {documents.length > 0 ? (
        <div className="space-y-2">
          {documents.map((doc, idx) => (
            <div
              key={(doc.key || doc.url) + idx}
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-white"
            >
              <FileText size={22} className="text-gray-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-700 truncate">{doc.name}</p>
                {doc.uploaded_by_name && (
                  <p className="text-xs text-gray-400">{doc.uploaded_by_name}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setViewing(doc)}
                className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-gray-50 rounded-lg"
                title="Visualizar"
              >
                <Eye size={18} />
              </button>
              <a
                href={doc.url}
                download={doc.name}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-gray-50 rounded-lg"
                title="Baixar"
              >
                <DownloadSimple size={18} />
              </a>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-50 rounded-lg"
                  title="Remover"
                >
                  <Trash size={18} />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        !canEdit && (
          <div className="text-center py-8">
            <FileText size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">{emptyText}</p>
          </div>
        )
      )}

      {canEdit && (
        <div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-emerald-400 hover:text-emerald-600 transition-colors disabled:opacity-60"
          >
            {uploading ? (
              <Spinner size={18} className="animate-spin" />
            ) : (
              <UploadSimple size={18} />
            )}
            {uploading ? 'Enviando...' : 'Anexar documento'}
          </button>
          <p className="text-xs text-gray-400 mt-1.5">
            PDF, Word, Excel ou imagem — até 10MB cada.
          </p>
          {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
        </div>
      )}

      {viewing && (
        <DocumentViewer
          isOpen={!!viewing}
          onClose={() => setViewing(null)}
          url={viewing.url}
          fileName={viewing.name}
          mimeType={viewing.mimeType || ''}
        />
      )}
    </div>
  )
}

export default DocumentAttachments
